import i18next from 'i18next';

import { type FileFormatClass } from './FileFormats';

import { isNullOrEmpty, isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';

interface LocaleEntry {
    key: string;
    language: string;
    country: string;
}

/**
 * Do not change order of `en,de,fr,nl,cs,da,it,no,pl,pt,ru,ro,es,sv,tr`
 *
 * Source: https://www.localeplanet.com/icu/
 */
const LANGUAGE_LOCALE_LIST: Array<LocaleEntry> = [
    { key: 'de',  language: 'de', country: 'DE'},
    { key: 'en',  language: 'en', country: 'US'},
    { key: 'fr',  language: 'fr', country: 'FR'},
    { key: 'nl',  language: 'nl', country: 'NL'},
    { key: 'cs',  language: 'cs', country: 'CZ'},
    { key: 'da',  language: 'da', country: 'DK'},
    { key: 'it',  language: 'it', country: 'IT'},
    { key: 'no',  language: 'nn', country: 'NO'},
    { key: 'pl',  language: 'pl', country: 'PL'},
    { key: 'pt',  language: 'pt', country: 'PT'},
    { key: 'ru',  language: 'ru', country: 'RU'},
    { key: 'ro',  language: 'ro', country: 'RO'},
    { key: 'es',  language: 'es', country: 'ES'},
    { key: 'sv',  language: 'sv', country: 'SE'},
    { key: 'tr',  language: 'tr', country: 'TR'},

    { key: 'bg',  language: 'bg', country: 'BG'},
    { key: 'fi',  language: 'fi', country: 'FI'},
    { key: 'ga',  language: 'ga', country: 'IE'},
    { key: 'hu',  language: 'hu', country: 'HU'},
    { key: 'lv',  language: 'lv', country: 'LV'},
    { key: 'sk',  language: 'sk', country: 'SK'},
];

type LanguageLocaleMap = Record<string, { language: string; country: string; }>;

function listToRecord(
    previous: LanguageLocaleMap,
    value: LocaleEntry,
): LanguageLocaleMap {
    return {...previous, [value.key]: { country: value.country, language: value.language }};
}

const LANGUAGE_LOCALE_MAP: LanguageLocaleMap = LANGUAGE_LOCALE_LIST.reduce(listToRecord, {});

export const DefaultDataLanguages: Array<string> = LANGUAGE_LOCALE_LIST.map((value) => value.key);

export function validDataLanguage(value: string): boolean {
    return DefaultDataLanguages.includes(value);
}

export function parseFilename(value: string): [string, string] {
    const index: number = value.lastIndexOf('.');
    if (index === -1) {
        return [value, ''];
    }

    return [value.substring(0, index), value.substring(index + 1)];
}

interface AsciiFilenameOptions {
    replaceCharacter?: string;
    extension?: string;
    extractExtension?: boolean;
    allowEmptyFilenames?: boolean;
    requireExtension?: boolean;
}

const MatchExtension: RegExp = new RegExp(/^([A-Za-z0-9]+)$/);
export function getAsciiFilename(value: string, options: AsciiFilenameOptions = {}): string {
    const { replaceCharacter = '_'} = options;

    let filename: string = value;
    let extension: string | undefined;

    if (options.extractExtension) {
        [filename, extension] = parseFilename(filename);
    }

    filename = convertUmlauts(filename).replace(/[^\w\-]/g, replaceCharacter);

    if (extension == null && options.extension != null) {
        extension = options.extension;
    }

    if (options.allowEmptyFilenames !== true && isNullOrEmpty(filename)) {
        throw new Error('invalid filename');
    }

    if (isNotNullOrEmpty(extension)) {
        if (!MatchExtension.test(extension)) {
            throw new Error('invalid extension');
        }

        return `${filename}.${extension}`;
    }

    if (options.requireExtension) {
        throw new Error('missing extension');
    }

    return filename;
}

function convertUmlauts(input: string): string {
    let output: string = '';

    for (const char of input) {
        switch (char) {
            case 'ä':
                output += 'ae';
                break;
            case 'Ä':
                output += 'Ae';
                break;
            case 'Ü':
                output += 'Ue';
                break;
            case 'ü':
                output += 'ue';
                break;
            case 'ö':
                output += 'oe';
                break;
            case 'Ö':
                output += 'Oe';
                break;
            case 'ß':
                output += 'ss';
                break;
            case 'á':
            case 'à':
            case 'â':
                output += 'a';
                break;
            case 'Á':
            case 'À':
            case 'Â':
                output += 'A';
                break;
            case 'é':
            case 'è':
            case 'ê':
            case 'ë':
                output += 'e';
                break;
            case 'É':
            case 'È':
            case 'Ê':
            case 'Ë':
                output += 'E';
                break;
            case 'í':
            case 'ì':
            case 'î':
            case 'ï':
                output += 'i';
                break;
            case 'Í':
            case 'Ì':
            case 'Î':
            case 'Ï':
                output += 'I';
                break;
            case 'ó':
            case 'ò':
            case 'ô':
                output += 'o';
                break;
            case 'Ó':
            case 'Ò':
            case 'Ô':
                output += 'O';
                break;
            case 'ú':
            case 'ù':
            case 'û':
                output += 'u';
                break;
            case 'Ú':
            case 'Ù':
            case 'Û':
                output += 'U';
                break;
            case 'ç':
                output += 'c';
                break;
            case 'ñ':
                output += 'n';
                break;
            default:
                output += char;
                break;
        }
    }

    return output;
}

export function prefixReturnValue<Args extends Array<unknown>>(prefix: string, cb: (...args: Args) => string): (...args: Args) => string {
    return (...args: Args): string => {
        return `${prefix}${cb(...args)}`;
    };
}

export function isDataUri(value: string | undefined | null): boolean {
    if (isNullOrEmpty(value)) {
        return false;
    }
    return /^\s*data:([a-z]+\/[a-z\+]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i.test(value);
}

/**
 * Use separator `_` (default-value) in eaiws context
 * @param language
 * @param separator
 * @returns
 */
export function convertLanguageToLocale(language?: string | null, separator: '-' | '_' = '_'): string {
    if (isNullOrEmpty(language)) {
        return ['en','US'].join(separator);
    }

    if (language.length === 2) {
        const value = LANGUAGE_LOCALE_MAP[language];
        return value != null ? [value.language, value.country].join(separator) : ['en','US'].join(separator);
    }

    return language.replace(/[-_]/, separator);
}

export function getDateString(value: string, language?: string): string {
    const dateTimeFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat(
        convertLanguageToLocale(language, '-'),
        { day: 'numeric', month: 'long', year: 'numeric'}
    );

    return dateTimeFormat.format(Date.parse(value));
}

export async function copyToClipBoard(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard != null) { // Chrome or FF with polyfills
            await navigator.clipboard.writeText(text);
            return true;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).clipboardData != null) { // Internet Explorer
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).clipboardData.setData('Text', text);
            return true;
        }

        return false;
    } catch {
        return false;
    }
}

export function getDateTimePartForFilename(): string {
    const currentDate: Date = new Date();
    const minutes: number = currentDate.getMinutes();
    const currentTime: string = `${currentDate.getHours()}${minutes > 10 ? minutes : `0${minutes}`}`;
    return `${currentDate.toLocaleDateString(i18next.language)}-${currentTime}`.replace(/[^\w\-]/g, '_');
}

export function isUrl(value: string | undefined): value is string {
    return isNotNullOrEmpty(value) && value.startsWith('http');
}

export function parseValidInt(value: string, fallback: number = 0): number {
    const result: number = parseInt(value, 10);

    if (result == null || isNaN(result)) {
        return fallback;
    }

    return result;
}

export function getFileExtension(value: string | undefined): string {
    if (isNotNullOrEmpty(value)) {
        const splittedName: Array<string> = value.split('.');
        if (splittedName.length > 0) {
            return splittedName[splittedName.length - 1];
        }
    }
    return '';
}

export function getFilename(baseName: string, format: FileFormatClass): string {
    return getAsciiFilename(baseName, { extension: format.extension});
}